import React, { useState, useRef, useEffect, memo } from 'react';
import { Columns3, X, Search, XCircle, Zap, Info, TriangleAlert, Copy, Signature, User } from 'lucide-react';

const QUICK_FILTER_PRESETS = (jiraName) => [
  {
    label: <><TriangleAlert size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Unlinked duplicates</>,
    filters: { resolution: ['Duplicate'], linkState: ['missing'] },
  },
  {
    label: <><Copy size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Resolved as Duplicate</>,
    filters: { resolution: ['Duplicate'] },
  },
  {
    label: <><Signature size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Reported by me</>,
    filters: jiraName ? { reporter: [jiraName] } : null,
  },
  {
    label: <><User size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Assigned to me</>,
    filters: jiraName ? { assignee: [jiraName] } : null,
  },
];

function isPresetActive(preset, filters) {
  const keys = Object.keys(preset.filters);
  return keys.every(k =>
    JSON.stringify([...(filters[k] ?? [])].sort()) === JSON.stringify([...preset.filters[k]].sort())
  ) && Object.keys(filters).every(k => keys.includes(k) || !filters[k]?.length);
}

function PresetsPopover({ jiraName, emptyFilters, filters, onFiltersChange, onClose, anchorRect }) {
  const ref = useRef(null);
  const presets = QUICK_FILTER_PRESETS(jiraName).filter(p => p.filters !== null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const style = anchorRect ? {
    position: 'fixed',
    top: anchorRect.bottom + 6,
    right: window.innerWidth - anchorRect.right,
    left: 'auto',
  } : {};

  return (
    <div className="presets-popup" ref={ref} style={style}>
      <div className="column-selector-header">
        <span>Quick filters</span>
        <button className="column-selector-close" onClick={onClose}><X size={14} /></button>
      </div>
      {presets.map((p, i) => {
        const active = isPresetActive(p, filters);
        return (
          <button
            key={i}
            className={`preset-popup-btn${active ? ' active' : ''}`}
            onClick={() => { onFiltersChange(active ? emptyFilters : { ...emptyFilters, ...p.filters }); onClose(); }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

const BugsToolbar = memo(function BugsToolbar({
  count,
  totalCount,
  search,
  onSearchChange,
  includeDesc,
  onIncludeDescChange,
  showAll,
  onShowAllChange,
  showUntriaged,
  onShowUntriagedChange,
  jiraName,
  filters,
  onFiltersChange,
  emptyFilters,
  onToggleColumns,
}) {
  const [showPresets, setShowPresets] = useState(false);
  const presets = QUICK_FILTER_PRESETS(jiraName).filter(p => p.filters !== null);
  const hasActivePreset = presets.some(p => isPresetActive(p, filters));
  const [localSearch, setLocalSearch] = useState(search ?? '');
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  function handleSearchChange(value) {
    setLocalSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchChange(value), 400);
  }

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const filtersApplied = hasActivePreset
    || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : !!v);

  return (
    <div className="issues-toolbar">
      <div className="issues-toolbar-left">
        <div className="issues-search">
          <Search size={14} className="issues-search-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search bugs…"
            value={localSearch}
            onChange={e => handleSearchChange(e.target.value)}
            className="issues-search-input"
          />
          {localSearch && (
            <button
              className="issues-search-clear"
              onClick={() => { handleSearchChange(''); inputRef.current?.focus(); }}
            >
              <XCircle size={14} />
            </button>
          )}
        </div>
        <label
          className={`issues-search-desc-toggle${localSearch ? '' : ' issues-search-desc-toggle--disabled'}`}
          title={localSearch ? 'Also match the search term against each bug’s description' : 'Type a search term above to use this'}
        >
          <input
            type="checkbox"
            checked={includeDesc}
            disabled={!localSearch}
            onChange={e => onIncludeDescChange(e.target.checked)}
          />
          incl. description
        </label>
        <label className="issues-search-desc-toggle">
          <input type="checkbox" checked={showAll} onChange={e => onShowAllChange(e.target.checked)} />
          Show bugs without matches
        </label>
        <label className="issues-search-desc-toggle" title="Untriaged bugs have no resolution set yet — unchecking narrows the table to confirmed duplicates missing their Jira link">
          <input type="checkbox" checked={showUntriaged} onChange={e => onShowUntriagedChange(e.target.checked)} />
          Show untriaged bugs
        </label>
      </div>

      <div className="issues-toolbar-right">
        <div className="issues-count-group">
          <span className="issues-count">
            <strong>{count}</strong>
            {totalCount != null && count !== totalCount ? <> / {totalCount}</> : null}
            {' '}bug{count !== 1 ? 's' : ''}
          </span>
          {filtersApplied ? (
            <span className="issues-filters-banner"><Info size={12} />Filters are applied</span>
          ) : null}
        </div>

        <div style={{ position: 'relative' }}>
          <button
            className={`issues-toolbar-btn${showPresets || hasActivePreset ? ' active' : ''}`}
            title="Quick filters"
            onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); setShowPresets(v => v ? false : rect); }}
          >
            <Zap size={15} style={{ flexShrink: 0 }} />
          </button>
          {showPresets && (
            <PresetsPopover
              jiraName={jiraName}
              filters={filters}
              emptyFilters={emptyFilters}
              onFiltersChange={onFiltersChange}
              onClose={() => setShowPresets(false)}
              anchorRect={showPresets}
            />
          )}
        </div>

        <button className="issues-toolbar-btn" title="Toggle columns" onClick={onToggleColumns}>
          <Columns3 size={15} style={{ flexShrink: 0 }} />
        </button>
      </div>
    </div>
  );
});

export default BugsToolbar;
